/**
 * AutoPopBlock — consolidated blue "Deal Info" cell.
 *
 * Replaces 8 individual columns (Sold Year/Make/Model, Salesperson,
 * Salesperson 2, Sales Manager, F&I Rep, Customer Last Name) with a single
 * 3-4 line text block that expands to inline edit on click. All 8 fields are
 * populated by the DealerBuilt deal-number lookup; the operator can still
 * override any value by entering edit mode. A read-only "RO:" line below the
 * customer name shows open repair orders on the sold unit (or "None") —
 * refreshed only by re-entering the deal #, not editable. Also hosts the
 * optional free-text deal comment (operator-entered, any Nut Sheet user).
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  DEAL_COMMENT_MAX,
  NutSheetRowDraft,
  isWholesaleRow,
  isSwapRow,
} from '../../types/nutSheet';
import { numCell, textCell } from './cellStyles';

interface AutoPopBlockProps {
  row: NutSheetRowDraft;
  onChange: (patch: Partial<NutSheetRowDraft>) => void;
  onCommit: () => void;
  saving: boolean;
}

export const AutoPopBlock: React.FC<AutoPopBlockProps> = ({
  row,
  onChange,
  onCommit,
  saving,
}) => {
  const [editing, setEditing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && firstInputRef.current) {
      firstInputRef.current.focus();
      firstInputRef.current.select();
    }
  }, [editing]);

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    // Stay in edit mode while focus moves between fields inside the block.
    if (
      containerRef.current &&
      !containerRef.current.contains(e.relatedTarget as Node | null)
    ) {
      setEditing(false);
      onCommit();
    }
  };

  const handleText =
    (field: keyof NutSheetRowDraft) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ [field]: e.target.value } as Partial<NutSheetRowDraft>);
    };

  if (editing) {
    return (
      <div
        ref={containerRef}
        onBlur={handleBlur}
        className="bg-blue-100 border border-blue-400 rounded p-1.5 space-y-1 min-w-[240px]"
      >
        <div className="grid grid-cols-3 gap-1">
          <input
            ref={firstInputRef}
            type="text"
            inputMode="numeric"
            placeholder="Year"
            value={row.soldYear ?? ''}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '');
              onChange({ soldYear: digits === '' ? null : parseInt(digits, 10) });
            }}
            className={numCell}
          />
          <input
            type="text"
            placeholder="Make"
            value={row.soldMake ?? ''}
            onChange={handleText('soldMake')}
            className={textCell}
            />
          <input
            type="text"
            placeholder="Model"
            value={row.soldModel ?? ''}
            onChange={handleText('soldModel')}
            className={textCell}
            />
        </div>
        <input
          type="text"
          placeholder="Salesperson"
          value={row.salesperson ?? ''}
          onChange={handleText('salesperson')}
          className={textCell}
        />
        <input
          type="text"
          placeholder="2nd Salesperson (optional)"
          value={row.salesperson2 ?? ''}
          onChange={handleText('salesperson2')}
          className={textCell}
        />
        <input
          type="text"
          placeholder="Sales Manager"
          value={row.salesManager ?? ''}
          onChange={handleText('salesManager')}
          className={textCell}
        />
        <input
          type="text"
          placeholder="F&I Rep"
          value={row.fiRep ?? ''}
          onChange={handleText('fiRep')}
          className={textCell}
        />
        <input
          type="text"
          placeholder="Customer Last Name"
          value={row.customerLastName ?? ''}
          onChange={handleText('customerLastName')}
          className={textCell}
        />
        <input
          type="text"
          inputMode="numeric"
          placeholder="Days in stock"
          value={row.daysInStock ?? ''}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '');
            onChange({ daysInStock: digits === '' ? null : parseInt(digits, 10) });
          }}
          className={numCell}
        />
        <textarea
          placeholder="Comment (optional)"
          rows={2}
          maxLength={DEAL_COMMENT_MAX}
          value={row.comment ?? ''}
          onChange={(e) => onChange({ comment: e.target.value })}
          className={`${textCell} resize-none`}
        />
      </div>
    );
  }

  // Text view
  const hasAnyData =
    row.soldYear != null ||
    !!row.soldMake ||
    !!row.soldModel ||
    !!row.salesperson ||
    !!row.salesperson2 ||
    !!row.salesManager ||
    !!row.fiRep ||
    !!row.customerLastName ||
    !!row.comment?.trim() ||
    row.daysInStock != null;

  const vehicleLine = [row.soldYear ?? '', row.soldMake ?? '', row.soldModel ?? '']
    .filter((p) => p !== '' && p != null)
    .join(' ')
    .trim();

  // Append the auto-populated age, e.g. "2021 Honda Civic · 47 days".
  const daysText = row.daysInStock != null ? `${row.daysInStock} days` : '';
  const vehicleDisplay = [vehicleLine, daysText].filter(Boolean).join(' · ');

  const soldByNames = [row.salesperson, row.salesperson2].filter(Boolean) as string[];
  const soldByLine = soldByNames.length > 0 ? `Sold: ${soldByNames.join(' & ')}` : '';

  const mgrFiSegments: string[] = [];
  if (row.salesManager) mgrFiSegments.push(`Mgr: ${row.salesManager}`);
  if (row.fiRep) mgrFiSegments.push(`F&I: ${row.fiRep}`);
  const mgrFiLine = mgrFiSegments.join(' • ');

  const customerLine = row.customerLastName ? `Cust: ${row.customerLastName}` : '';
  const commentText = row.comment?.trim() ?? '';

  // CSI bonus toggle — retail rows only (no retail customer survey on wholesale/swap
  // dealer trades). Gated on a chosen Type so it stays off brand-new empty rows.
  const showCsi = !!row.type && !isWholesaleRow(row) && !isSwapRow(row);
  const csiOn = !!row.csiBonus;

  return (
    <div
      ref={containerRef}
      onClick={() => !saving && setEditing(true)}
      className="relative bg-blue-100 border border-blue-400 rounded px-2 py-1 cursor-pointer hover:ring-1 hover:ring-blue-500 text-xs leading-tight min-w-[240px]"
      title="Click to edit"
    >
      {showCsi && (
        <button
          type="button"
          disabled={saving}
          onClick={(e) => {
            e.stopPropagation(); // don't open inline edit
            onChange({ csiBonus: !csiOn });
          }}
          className={`absolute top-1 right-1 px-1.5 py-0.5 rounded text-[10px] font-bold leading-none border transition-colors disabled:opacity-50 ${
            csiOn
              ? 'bg-green-500 border-green-600 text-white hover:bg-green-600'
              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
          }`}
          title={csiOn ? 'CSI bonus: paid (click to clear)' : 'CSI bonus: not paid (click to set)'}
        >
          CSI
        </button>
      )}
      {!hasAnyData ? (
        <span className="italic text-gray-400">Enter deal # to auto-fill</span>
      ) : (
        <>
          {vehicleDisplay && <div className="font-semibold text-gray-900 pr-9">{vehicleDisplay}</div>}
          {soldByLine && <div className="text-gray-700">{soldByLine}</div>}
          {mgrFiLine && <div className="text-gray-700">{mgrFiLine}</div>}
          {customerLine && <div className="text-gray-700">{customerLine}</div>}
          {commentText && (
            <div className="italic text-gray-600 whitespace-pre-wrap break-words">
              💬 {commentText}
            </div>
          )}
          <div className="text-gray-700">
            RO:{' '}
            {row.openROs ? (
              <span className="font-bold text-red-800">{row.openROs}</span>
            ) : (
              'None'
            )}
          </div>
        </>
      )}
    </div>
  );
};
